import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getRoleConfig } from 'common-src/features/auth';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import { getAppointmentDetailsFromAthena } from 'common-src/models/PatientIntegrationsAthena';
import PatientOncehubBooking, {
  OncehubBookingStatuses,
  updateOncehubBookingStatus,
} from 'common-src/models/PatientOncehubBooking';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import { ModalType } from 'src/components/base/ModalGroup';
import { ActionsRow, CreatorItem, TablePagination, TextItem, TimeItem } from 'src/components/table';
import BaseTable from 'src/components/table/BaseTable';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import { openModal } from 'src/features/modals';
import { getScheduledCallTypeOrEventName } from 'src/fragments/ScheduledCallsFragment/helpers';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useOpenWindow from 'src/hooks/useOpenWindow';
import useTabs from 'src/hooks/useTabs';

import { DefaultTimezone, ScheduledCallStatus } from '../constants';
import { getCallStatus, getHeaders } from './helpers';
import { StatusItem } from './items';
import styles from './ScheduledCallsList.module.scss';

const ScheduledCallsList = ({ patientId, scheduledCalls = [] }) => {
  const dispatch = useDispatch();

  const [types, setTypes] = useState([]);
  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });

  const patient = useCustomSelector((state) => singleModelSelector(state, 'Patient', patientId));
  const actions =
    useCustomSelector((state) => getRoleConfig(state)?.patientChart?.sections?.scheduledCalls) ||
    {};
  const isAthenaEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'athena'));

  const { openWindow } = useOpenWindow();
  const { onSelectTab } = useTabs();
  const { showAlert, AlertType } = useAlert();

  const timeZone = patient?.timeZone ?? DefaultTimezone;

  const viewAppointmentDetails = (athenaAppointmentId) => {
    if (!athenaAppointmentId) {
      showAlert(AlertType.Warning, '', 'No appointment ID available.');
      return;
    }

    dispatch(
      getAppointmentDetailsFromAthena(athenaAppointmentId, {
        successBlock: (data) => {
          const detailsData = Array.isArray(data) ? data[0] : data;

          if (!detailsData) {
            showAlert(AlertType.Warning, '', 'No appointment found with that ID.');
            return;
          }

          // Open modal with appointment details
          dispatch(
            openModal(ModalType.APPOINTMENT_DETAILS, {
              appointmentDetails: detailsData,
            }),
          );
        },
        errorBlock: (err) => {
          showAlert(AlertType.Error, '', `Failed to fetch appointment details: ${err}`);
        },
      }),
    );
  };

  const getFilteredScheduledCalls = () => {
    if (_.isEmpty(types)) return scheduledCalls;
    return scheduledCalls.filter((c) => {
      const visitType = c?.isAthenaAppointment ? c?._fields?.visitType : c?.visitType;
      return types.some((t) => visitType === t);
    });
  };

  const headers = getHeaders(types, setTypes, patientId);
  const filteredScheduledCalls = useMemo(
    () => getFilteredScheduledCalls(),
    [scheduledCalls, types],
  );

  const getData = () =>
    filteredScheduledCalls.map((item) => {
      const status = getCallStatus(item, timeZone);
      const patientId = patient?.id ?? item?.ref?.patientId;

      return {
        scheduledTime: (
          <TimeItem
            date={item.isAthenaAppointment ? item._fields?.scheduledAt : item.scheduledAt}
            duration={
              item.isAthenaAppointment ? item._fields?.durationMinutes : item.durationMinutes
            }
            timeZone={timeZone}
          />
        ),
        createdTime: (
          <TimeItem
            date={item.isAthenaAppointment ? item._fields?.createdAt : item.createdAt}
            timeZone={timeZone}
          />
        ),
        patientId: (
          <TextItem
            isActive
            text={patientId ?? 'Not assigned'}
            isUnderlined={!!patientId}
            onClick={() => {
              if (!patientId) return;
              onSelectTab({
                path: `/dashboard-client/patients/details/${patientId}/scheduled-calls`,
              });
            }}
          />
        ),
        type: getScheduledCallTypeOrEventName(item),
        reason: (
          <TextItem
            isActive
            text={item.isAthenaAppointment ? item._fields?.visitReason : item.visitReason || '-'}
          />
        ),
        assigned: (
          <CreatorItem
            isActive
            creator={item?.client}
            creatorName={item?.client?.getName(false, true)}
          />
        ),
        status: <StatusItem status={status} />,
        actions: (
          <ActionsRow
            options={_.compact([
              item.isAthenaAppointment &&
                isAthenaEnabled && {
                  id: 'view-appointment-details-button',
                  text: 'View Details',
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  onClick: () => viewAppointmentDetails(item.athenaData?.athenaAppointmentId),
                },

              item.isAthenaAppointment &&
                actions?.canEditScheduledCall && {
                  id: 'edit-athena-button',
                  text: 'Edit',
                  backgroundColor: '#555671B2',
                  color: 'white',
                  onClick: () =>
                    dispatch(
                      openModal(ModalType.EDIT_VISIT, {
                        scheduledCall: item,
                        timeZone,
                        patientId,
                        // For Athena visits we only support editing caregivers (attendees)
                        caregiversOnly: true,
                      }),
                    ),
                },

              !item.isAthenaAppointment && {
                id: 'modify-button',
                text: 'Modify',
                backgroundColor: '#555671B2',
                color: 'white',
                disabled:
                  ![
                    ScheduledCallStatus.TODAY,
                    ScheduledCallStatus.SCHEDULED,
                    ScheduledCallStatus.RESCHEDULED,
                  ].includes(status) || !item?.rescheduleUrl,
                onClick: () => openWindow({ url: item.rescheduleUrl, target: '_blank' }),
              },

              !item.isAthenaAppointment && {
                id: 'mark-as-completed-button',
                text: 'Mark as Completed',
                backgroundColor: '#375844CC',
                color: 'white',
                disabled: !['missed'].includes(status),
                onClick: () =>
                  dispatch(
                    updateOncehubBookingStatus(
                      item.id,
                      OncehubBookingStatuses.completed.text.toLowerCase(),
                    ),
                  ),
              },

              !item.isAthenaAppointment && {
                id: 'mark-as-missed-button',
                text: 'Mark as Missed',
                backgroundColor: 'rgba(240, 31, 81)',
                color: 'white',
                disabled: !['completed'].includes(status),
                onClick: () =>
                  dispatch(
                    updateOncehubBookingStatus(
                      item.id,
                      OncehubBookingStatuses.missed.text.toLowerCase(),
                    ),
                  ),
              },

              !item.isAthenaAppointment &&
                actions?.canEditScheduledCall && {
                  id: 'edit-button',
                  text: 'Edit',
                  backgroundColor: '#555671B2',
                  color: 'white',
                  onClick: () =>
                    dispatch(
                      openModal(ModalType.EDIT_VISIT, {
                        scheduledCall: item,
                        timeZone,
                        patientId,
                      }),
                    ),
                },
            ])}
          />
        ),
      };
    });

  return (
    <div className={patientId ? styles.patientContainer : styles.nonPatientContainer}>
      <BaseTable
        headers={headers}
        data={getData()}
        name="scheduled-calls"
        classNames={[
          styles.table,
          filteredScheduledCalls?.length <= ResultsCount[25] ? styles.noPagination : '',
        ]}
        emptyText="No scheduled visits"
        emptyClassNames={[styles.empty]}
        paginationData={paginationData}
        initialSortColumn={{
          column: 'scheduledTime',
          label: 'Scheduled Time',
          width: '20%',
          sortBy: 'date',
        }}
      />
      <TablePagination onChangePageHandler={setPaginationData} results={filteredScheduledCalls} />
    </div>
  );
};

ScheduledCallsList.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scheduledCalls: PropTypes.arrayOf(PropTypes.shape(PatientOncehubBooking.schema)),
};

export default ScheduledCallsList;
